import { GroupBase, StylesConfig } from "react-select";

/**
 * react-select paints its own control and menu (white by default), so on the dark theme every
 * select was white. These styles take their colours from the theme's CSS variables, which both
 * themes define (`--vz-input-bg`, `--vz-input-border`, `--vz-body-color`, `--vz-light-rgb`, ...),
 * so light mode keeps its look and dark mode follows. Bootstrap's dropdown variables are not used:
 * the dark theme scopes those to `.dropdown-menu`.
 */
export const themedSelectStyles: StylesConfig<any, boolean, GroupBase<any>> = {
    control: (base, state) => ({
        ...base,
        backgroundColor: state.isDisabled ? 'var(--vz-input-disabled-bg)' : 'var(--vz-input-bg)',
        borderColor: state.isFocused ? 'var(--vz-input-focus-border)' : 'var(--vz-input-border)',
        boxShadow: 'none',
        color: 'var(--vz-body-color)',
        ':hover': { borderColor: state.isFocused ? 'var(--vz-input-focus-border)' : 'var(--vz-input-border)' },
    }),
    menu: (base) => ({
        ...base,
        backgroundColor: 'var(--vz-input-bg)',
        border: '1px solid var(--vz-border-color)',
        boxShadow: '0 5px 10px rgba(30, 32, 37, 0.12)',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'var(--vz-primary)'
            : state.isFocused ? 'rgba(var(--vz-light-rgb), 0.75)' : 'transparent',
        color: state.isSelected ? '#fff' : 'var(--vz-body-color)',
        ':active': { backgroundColor: state.isSelected ? 'var(--vz-primary)' : 'rgba(var(--vz-light-rgb), 1)' },
    }),
    singleValue: (base) => ({ ...base, color: 'var(--vz-body-color)' }),
    input: (base) => ({ ...base, color: 'var(--vz-body-color)' }),
    multiValue: (base) => ({ ...base, backgroundColor: 'rgba(var(--vz-light-rgb), 1)' }),
    multiValueLabel: (base) => ({ ...base, color: 'var(--vz-body-color)' }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: 'var(--vz-input-border)' }),
};

/**
 * themedSelectStyles with a component's own styles applied on top: each of `extra`'s functions
 * receives the themed result as its base.
 */
export function withThemedSelectStyles<O, M extends boolean, G extends GroupBase<O>>(
    extra: StylesConfig<O, M, G>
): StylesConfig<O, M, G> {
    const themed = themedSelectStyles as unknown as StylesConfig<O, M, G>;
    const merged: Record<string, any> = { ...themed };

    for (const key of Object.keys(extra) as (keyof StylesConfig<O, M, G>)[]) {
        const own = extra[key] as any;
        const base = themed[key] as any;
        merged[key] = base ? (styles: any, state: any) => own(base(styles, state), state) : own;
    }

    return merged as StylesConfig<O, M, G>;
}
